import {
  ArrowLeftIcon,
  CheckCircleIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getPoliceReportStatusConfig,
  hasPoliceReportEvidence,
} from '@/components/police/utils/policeUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import type { DetailedPoliceReport } from '@/services/api/generated/policeReportsService';
import {
  usePoliceDepartmentsTriggerEnrichmentMutation,
  usePoliceReportsDestroyMutation,
  usePoliceReportsEnsureTasksMutation,
} from '@/services/api/police';

import { PoliceDetailsHeader } from './PoliceDetailsHeader';
import { PoliceDetailsInfo } from './PoliceDetailsInfo';
import { EvidenceSection } from './sections/EvidenceSection';
import { OfficersSection } from './sections/OfficersSection';
import { PoliceDepartmentSection } from './sections/PoliceDepartmentSection';

interface PoliceDetailsProps {
  matterId: string;
  report: DetailedPoliceReport;
  onBack: () => void;
  onUpdate: () => void;
  backButtonText: string;
}

export const PoliceDetails = ({
  matterId,
  report,
  onBack,
  onUpdate,
  backButtonText,
}: PoliceDetailsProps) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEnsuringTasks, setIsEnsuringTasks] = useState(false);
  const [isEnrichingDepartment, setIsEnrichingDepartment] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletePoliceReport] = usePoliceReportsDestroyMutation();
  const [ensurePoliceReportTasks] = usePoliceReportsEnsureTasksMutation();
  const [triggerEnrichment] = usePoliceDepartmentsTriggerEnrichmentMutation();

  const handleEdit = useCallback(() => {
    if (!matterId?.trim() || !report?.id) return;

    try {
      navigate(
        `/matters/${encodeURIComponent(matterId.trim())}/police/${encodeURIComponent(report.id)}/edit`,
      );
    } catch (err) {
      console.error('Navigation error:', err);
      toast.error('Failed to navigate to edit page');
    }
  }, [matterId, report?.id, navigate]);

  const handleDelete = useCallback(async () => {
    if (isDeleting || !matterId?.trim() || !report?.id) return; // Prevent double-clicks

    setIsDeleting(true);
    try {
      await deletePoliceReport({
        matterId: matterId.trim(),
        id: report.id,
      }).unwrap();

      toast.success('Police report deleted successfully');

      // Call onUpdate first, then navigate back
      try {
        await onUpdate();
      } catch (updateError) {
        console.warn('Failed to update after deletion:', updateError);
        // Don't block navigation on update failure
      }

      onBack(); // Navigate back to list after deletion
    } catch (error: any) {
      console.error('Failed to delete police report:', error);

      // Extract meaningful error message
      const errorMessage =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        'Failed to delete police report';

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [deletePoliceReport, matterId, report?.id, onUpdate, onBack, isDeleting]);

  const handleBackClick = useCallback(() => {
    try {
      onBack();
    } catch (err) {
      console.error('Back navigation error:', err);
      toast.error('Failed to navigate back');
    }
  }, [onBack]);

  const handleUpdateWrapper = useCallback(async () => {
    try {
      await onUpdate();
      toast.success('Police report updated successfully');
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update police report');
    }
  }, [onUpdate]);

  const handleEnsureTasks = useCallback(async () => {
    if (isEnsuringTasks || !matterId?.trim() || !report?.id) return;

    setIsEnsuringTasks(true);
    try {
      await ensurePoliceReportTasks({
        matterId: matterId.trim(),
        id: report.id,
      }).unwrap();

      toast.success('Tasks ensured successfully');
    } catch (error: any) {
      console.error('Failed to ensure tasks:', error);

      const errorMessage =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        'Failed to ensure tasks';

      toast.error(errorMessage);
    } finally {
      setIsEnsuringTasks(false);
    }
  }, [ensurePoliceReportTasks, matterId, report?.id, isEnsuringTasks]);

  const handleEnrichDepartment = useCallback(async () => {
    if (isEnrichingDepartment || !report?.police_department?.id) return;

    setIsEnrichingDepartment(true);
    try {
      await triggerEnrichment({
        id: report.police_department.id,
      }).unwrap();

      toast.success('Department enrichment started');
      await onUpdate(); // Refresh data
    } catch (error: any) {
      console.error('Failed to trigger enrichment:', error);

      const errorMessage =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        'Failed to start enrichment';

      toast.error(errorMessage);
    } finally {
      setIsEnrichingDepartment(false);
    }
  }, [
    triggerEnrichment,
    report?.police_department?.id,
    onUpdate,
    isEnrichingDepartment,
  ]);

  // Validate required props after all hooks are called
  if (!matterId?.trim()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900">Invalid Matter</h2>
          <p className="mt-2 text-gray-600">No valid matter ID provided.</p>
        </div>
      </div>
    );
  }

  if (!report?.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900">
            Report Not Found
          </h2>
          <p className="mt-2 text-gray-600">
            The requested police report could not be found.
          </p>
          <Button variant="outline" onClick={onBack} className="mt-4">
            {backButtonText}
          </Button>
        </div>
      </div>
    );
  }

  // Check if we have evidence to show
  const hasEvidence = hasPoliceReportEvidence(report as any);

  // Get status configuration for better display
  const statusConfig = getPoliceReportStatusConfig(
    report.attestation_status as any,
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackClick}
              className="flex items-center space-x-1"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span>{backButtonText}</span>
            </Button>
            <PoliceDetailsHeader report={report as DetailedPoliceReport} />
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="flex items-center space-x-1"
              disabled={isDeleting}
            >
              <PencilIcon className="h-4 w-4" />
              <span>Edit</span>
            </Button>

            <Dropdown>
              <DropdownButton
                as="button"
                className="p-1.5 rounded-md hover:bg-gray-100 flex items-center justify-center border border-gray-300"
                disabled={
                  isDeleting || isEnsuringTasks || isEnrichingDepartment
                }
              >
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenu>
                <DropdownItem
                  onClick={handleEnrichDepartment}
                  disabled={
                    isDeleting || isEnsuringTasks || isEnrichingDepartment
                  }
                  className="flex items-center gap-2"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" data-slot="icon" />
                  {isEnrichingDepartment
                    ? 'Finding Info...'
                    : 'Find Contact Info'}
                </DropdownItem>
                <DropdownItem
                  onClick={handleEnsureTasks}
                  disabled={
                    isDeleting || isEnsuringTasks || isEnrichingDepartment
                  }
                  className="flex items-center gap-2"
                >
                  <CheckCircleIcon className="h-4 w-4" data-slot="icon" />
                  {isEnsuringTasks ? 'Ensuring...' : 'Ensure Tasks'}
                </DropdownItem>
                <DropdownItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={
                    isDeleting || isEnsuringTasks || isEnrichingDepartment
                  }
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" data-slot="icon" />
                  {isDeleting ? 'Deleting...' : 'Delete Report'}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mx-auto max-w-7xl">
          {hasEvidence ? (
            /* Two-column layout with evidence */
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left Column - Police Report Details */}
              <div className="lg:col-span-2 space-y-6">
                <PoliceDetailsInfo
                  report={report}
                  onUpdate={handleUpdateWrapper}
                  matterId={matterId}
                />

                {report.involved_officers &&
                  report.involved_officers.length > 0 && (
                    <OfficersSection officers={report.involved_officers} />
                  )}
              </div>

              {/* Right Column - Police Department & Evidence */}
              <div className="lg:col-span-3 space-y-6">
                <PoliceDepartmentSection
                  department={report.police_department}
                />

                <EvidenceSection report={report as any} matterId={matterId} />
              </div>
            </div>
          ) : (
            /* Single column layout without evidence */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <PoliceDetailsInfo
                  report={report}
                  onUpdate={handleUpdateWrapper}
                  matterId={matterId}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <PoliceDepartmentSection
                  department={report.police_department}
                />

                {report.involved_officers &&
                  report.involved_officers.length > 0 && (
                    <OfficersSection officers={report.involved_officers} />
                  )}

                <EvidenceSection report={report as any} matterId={matterId} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Police Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this police report? This action
              cannot be undone.
              {hasEvidence && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  <strong>Warning:</strong> This report has associated evidence
                  and citations that will also be affected.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Report'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
